import React, { useState, useMemo } from 'react';
import './RoadmapTimeline.scss';
import featureJson from './features.json';

type RoleView = 'Dev' | 'Product' | 'CEO' | 'TeamLead' | 'Sales';

interface UserStory {
  'User Story': string;
  'Starting Point (incl. Problem Description)': string;
  'Possible Solution': string;
}

interface Feature {
  id: number;
  title: string;
  epic: string;
  color: string;
  start: number;
  end: number;
  responsible: string;
  reviewer: string;
  stage: string;
  status: 'Done' | 'Planned' | 'In Planung' | 'In Arbeit' | 'Fertig';
  description: string;
  userStory: UserStory;
  acceptance: string[];
  effort: string;
  benefit: string;
  demo: string;
  prod: string;
  deps: number[];
}

const FEATURES: Feature[] = (featureJson as any[]).map(f => ({
  ...f,
  deps: Array.isArray(f.deps) ? f.deps.map((d: any) => Number(d)) : [],
}));

export const RoadmapTimeline: React.FC = () => {
  const [view, setView] = useState<RoleView>('Dev');
  const [epicFilter, setEpicFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [respFilter, setRespFilter] = useState<string>('');
  const [revFilter, setRevFilter] = useState<string>('');
  const [modalFeature, setModalFeature] = useState<Feature | null>(null);

  // derive unique lists
  const epics = useMemo(() => Array.from(new Set(FEATURES.map(f => f.epic))), []);
  const responsibles = useMemo(() => Array.from(new Set(FEATURES.map(f => f.responsible))), []);
  const reviewers = useMemo(() => Array.from(new Set(FEATURES.map(f => f.reviewer))), []);

  // compute KW range dynamically
  const [minKW, maxKW] = useMemo(() => {
    const starts = FEATURES.map(f => f.start);
    const ends = FEATURES.map(f => f.end);
    return [Math.min(...starts), Math.max(...ends)];
  }, []);

  const totalColumns = maxKW - minKW + 1;

  // apply all filters
  const filtered = FEATURES.filter(f =>
    (!epicFilter || f.epic === epicFilter) &&
    (!statusFilter || f.status === statusFilter) &&
    (!respFilter || f.responsible === respFilter) &&
    (!revFilter || f.reviewer === revFilter)
  );

  return (
    <div className="roadmap-timeline">
      {/* Controls */}
      <div className="controls">
        <select value={view} onChange={e => setView(e.target.value as RoleView)}>
          {['CEO', 'Product', 'Dev', 'TeamLead', 'Sales'].map(r =>
            <option key={r} value={r}>{r}</option>
          )}
        </select>
        <select value={epicFilter} onChange={e => setEpicFilter(e.target.value)}>
          <option value="">All Epics</option>
          {epics.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Status</option>
          {['Done', 'Planned', 'In Planung', 'In Arbeit', 'Fertig'].map(s =>
            <option key={s} value={s}>{s}</option>
          )}
        </select>
        <select value={respFilter} onChange={e => setRespFilter(e.target.value)}>
          <option value="">All Responsibles</option>
          {responsibles.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={revFilter} onChange={e => setRevFilter(e.target.value)}>
          <option value="">All Reviewers</option>
          {reviewers.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      {/* Legend */}
      <div className="legend">
        {epics.map(ep => (
          <div key={ep} className="legend-item">
            <span
              className="legend-color"
              style={{ background: FEATURES.find(f => f.epic === ep)!.color }}
            />
            {ep}
          </div>
        ))}
      </div>

      {/* Timeline Grid */}
      <div className="timeline-container">
        <div
          className="timeline"
          style={{ gridTemplateColumns: `repeat(${totalColumns}, 1fr)` }}
        >
          {Array.from({ length: totalColumns }, (_, i) => (
            <div key={i} className="header">KW{minKW + i}</div>
          ))}

          {filtered.map(f => (
            <div
              key={f.id}
              className="bar"
              style={{
                gridColumn: `${f.start - minKW + 1} / span ${f.end - f.start + 1}`,
                background: f.color
              }}
              onClick={() => setModalFeature(f)}
            >
              {f.title} <strong>{f.responsible}</strong>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      {modalFeature && (
        <div className="modal-backdrop" onClick={() => setModalFeature(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setModalFeature(null)}>
              &times;
            </button>
            <h2>{modalFeature.title}</h2>

            <section>
              <h3>Description</h3>
              <p>{modalFeature.description}</p>
            </section>

            {(view === 'Dev' || view === 'Product') && (
              <section>
                <h3>User Story</h3>
                <p>{modalFeature.userStory['User Story']}</p>
                <h4>Starting Point (incl. Problem Description)</h4>
                <p>{modalFeature.userStory['Starting Point (incl. Problem Description)']}</p>
                <h4>Possible Solution</h4>
                <p>{modalFeature.userStory['Possible Solution']}</p>
              </section>
            )}

            {view === 'Dev' && (
              <section>
                <h3>Acceptance Criteria</h3>
                <ul>
                  {modalFeature.acceptance.map((ac, i) => <li key={i}>{ac}</li>)}
                </ul>
              </section>
            )}

            <section>
              <h3>Metadata</h3>
              <table>
                <tbody>
                  <tr><td>Responsible</td><td>{modalFeature.responsible}</td></tr>
                  <tr><td>Reviewer</td><td>{modalFeature.reviewer}</td></tr>
                  <tr><td>Effort</td><td>{modalFeature.effort}</td></tr>
                  <tr><td>Benefit</td><td>{modalFeature.benefit}</td></tr>
                  <tr><td>Demo</td><td>{modalFeature.demo}</td></tr>
                  <tr><td>Prod</td><td>{modalFeature.prod}</td></tr>
                  <tr><td>Stage</td><td>{modalFeature.stage}</td></tr>
                  <tr><td>Status</td><td>{modalFeature.status}</td></tr>
                </tbody>
              </table>
            </section>

            {(view === 'Dev' || view === 'Product') && (
              <section>
                <h3>Dependencies</h3>
                <ul>
                  {modalFeature.deps.length > 0
                    ? modalFeature.deps.map(d => {
                        const dep = FEATURES.find(x => x.id === d);
                        return <li key={d}>{dep?.title || `Feature ${d}`}</li>;
                      })
                    : <li>None</li>
                  }
                </ul>
              </section>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RoadmapTimeline;
